import { api, unwrap } from "@lumberset/api-client";
import { queryOptions, useQueryClient } from "@tanstack/react-query";

/** One workspace's page in the Admin panel, and the calls behind what an Admin can do to it. */

const fetchWorkspace = (id: string) => unwrap(api.admin.workspaces[":id"].$get({ param: { id } }));

export type WorkspaceDetail = Awaited<ReturnType<typeof fetchWorkspace>>;

export const workspaceQuery = (id: string) =>
  queryOptions({ queryKey: ["admin", "workspaces", id], queryFn: () => fetchWorkspace(id) });

export type MemberRole = "owner" | "admin" | "member";

/**
 * What becomes of the Owner a workspace had when an Admin makes someone else
 * Owner: a workspace has one, so they become a Manager or are removed.
 */
export type FormerOwner = "manager" | "remove";

/** Where an Admin can sign in as someone: the Yard app as a Member, or the Staff panel as Staff. */
export type SignInSurface = "app" | "ops";

/**
 * Sign in as: a one-time link that opens the other app as this person, in
 * the Yard app inside the workspace given. Answers with the `url` to open in
 * a new tab, good for a minute.
 */
export const signInAs = (userId: string, surface: SignInSurface, yardId?: string) =>
  unwrap(api.admin.impersonations.$post({ json: { userId, surface, yardId } }));

/** A workspace's name, the details on its quotes, and its kind, as an Admin edits them. */
export type WorkspacePatch = Parameters<(typeof api.admin.workspaces)[":id"]["$patch"]>[0]["json"];

/** A workspace's own Reading limit and its AI reading allowance, as an Admin sets them. */
export type ReadingTermsInput = Parameters<
  (typeof api.admin.workspaces)[":id"]["reading"]["$put"]
>[0]["json"];

/** Each call answers with a `message`: one sentence for the toast saying what happened. */
export const workspaceActions = {
  update: (id: string, json: WorkspacePatch) =>
    unwrap(api.admin.workspaces[":id"].$patch({ param: { id }, json })),
  /** Gives Credits, with the reason on the Ledger. Answers with the new total. */
  addCredits: (id: string, quotes: number, note: string) =>
    unwrap(api.admin.yards[":id"].credits.$post({ param: { id }, json: { quotes, note } })),
  /** Gives account credit, in cents, with the reason on the Ledger. Answers with the new balance. */
  addAccountCredit: (id: string, amountCents: number, note: string) =>
    unwrap(
      api.admin.yards[":id"]["account-credit"].$post({
        param: { id },
        json: { amountCents, note },
      }),
    ),
  /** Extends the trial, with the reason on the Ledger. Answers with the new total. */
  addTrialQuotes: (id: string, quotes: number, note: string) =>
    unwrap(api.admin.yards[":id"]["trial-quotes"].$post({ param: { id }, json: { quotes, note } })),
  /** Making someone Owner hands the workspace to them; `formerOwner` says what becomes of the last one. */
  changeRole: (id: string, userId: string, role: MemberRole, formerOwner?: FormerOwner) =>
    unwrap(
      api.admin.workspaces[":id"].members[":userId"].$patch({
        param: { id, userId },
        json: { role, formerOwner },
      }),
    ),
  /** A fresh link to choose a password, for a member who never chose one. */
  resendWelcome: (id: string, userId: string) =>
    unwrap(api.admin.workspaces[":id"].members[":userId"].welcome.$post({ param: { id, userId } })),
  /** The workspace's invitation again, good for 7 more days. */
  resendInvitation: (id: string, invitationId: string) =>
    unwrap(
      api.admin.workspaces[":id"].invitations[":invitationId"].resend.$post({
        param: { id, invitationId },
      }),
    ),
  removeMember: (id: string, userId: string) =>
    unwrap(api.admin.workspaces[":id"].members[":userId"].$delete({ param: { id, userId } })),
  blockSignIn: (userId: string, reason: string) =>
    unwrap(
      api.admin.workspaces.users[":userId"].ban.$post({ param: { userId }, json: { reason } }),
    ),
  restoreSignIn: (userId: string) =>
    unwrap(api.admin.workspaces.users[":userId"].unban.$post({ param: { userId } })),
  confirmEmail: (userId: string) =>
    unwrap(api.admin.workspaces.users[":userId"]["confirm-email"].$post({ param: { userId } })),
  sendConfirmation: (userId: string) =>
    unwrap(api.admin.workspaces.users[":userId"]["send-confirmation"].$post({ param: { userId } })),
  deleteProject: (id: string, projectId: string, input: { force?: boolean; reason?: string }) =>
    unwrap(
      api.admin.workspaces[":id"].projects[":projectId"].$delete({
        param: { id, projectId },
        json: input,
      }),
    ),
  pause: (id: string) => unwrap(api.admin.workspaces[":id"].pause.$post({ param: { id } })),
  reactivate: (id: string) =>
    unwrap(api.admin.workspaces[":id"].reactivate.$post({ param: { id } })),
  close: (id: string, reason: string) =>
    unwrap(
      api.admin.workspaces[":id"].close.$post({
        param: { id },
        json: { reason: reason.trim() || undefined },
      }),
    ),
  deleteForGood: (id: string, confirmName: string) =>
    unwrap(api.admin.workspaces[":id"].$delete({ param: { id }, json: { confirmName } })),
  /** Sends a takeoff that failed or stopped at the reading limit back to be read again. */
  readAgain: (id: string, runId: string) =>
    unwrap(
      api.admin.workspaces[":id"].runs[":runId"]["read-again"].$post({ param: { id, runId } }),
    ),
  /**
   * Its own Reading limit and its AI reading allowance all told, in cents.
   * LumberSet's alone; on the audit log. Answers with the terms as they stand.
   */
  setReading: (id: string, json: ReadingTermsInput) =>
    unwrap(api.admin.workspaces[":id"].reading.$put({ param: { id }, json })),
  /** Out of the platform's view and paused. The workspace's own app shows no change. */
  archive: (id: string, reason: string) =>
    unwrap(
      api.admin.workspaces[":id"].archive.$post({
        param: { id },
        json: { reason: reason.trim() || undefined },
      }),
    ),
  restore: (id: string) => unwrap(api.admin.workspaces[":id"].restore.$post({ param: { id } })),
  archiveProject: (id: string, projectId: string) =>
    unwrap(
      api.admin.workspaces[":id"].projects[":projectId"].archive.$post({
        param: { id, projectId },
      }),
    ),
  restoreProject: (id: string, projectId: string) =>
    unwrap(
      api.admin.workspaces[":id"].projects[":projectId"].restore.$post({
        param: { id, projectId },
      }),
    ),
  /** Keeps one of a client's workspaces and archives the others as they are. Nothing moves. */
  keepOne: (keep: string, others: string[]) =>
    unwrap(api.admin.workspaces.duplicates["keep-one"].$post({ json: { keep, others } })),
  /**
   * Moves everything of the others into the one kept, people included, and
   * folds the chosen sign-ins into its Owner. The kept workspace's name confirms.
   */
  combine: (keep: string, others: string[], confirmName: string, foldSignIns: string[]) =>
    unwrap(
      api.admin.workspaces.duplicates.combine.$post({
        json: { keep, others, confirmName, foldSignIns },
      }),
    ),
  /** Merges one person's account into another's and blocks the old sign-in. */
  mergeAccount: (userId: string, into: string) =>
    unwrap(
      api.admin.workspaces.users[":userId"].merge.$post({ param: { userId }, json: { into } }),
    ),
};

/** Workspaces in view that look like the same client as this one, with why. */
export const duplicatesQuery = (id: string) =>
  queryOptions({
    queryKey: ["admin", "workspaces", id, "duplicates"],
    queryFn: () => unwrap(api.admin.workspaces[":id"].duplicates.$get({ param: { id } })),
  });

const fetchDuplicatesPreview = (keep: string, others: string[]) =>
  unwrap(
    api.admin.workspaces.duplicates.preview.$get({ query: { keep, others: others.join(",") } }),
  );

export type DuplicatesPreview = Awaited<ReturnType<typeof fetchDuplicatesPreview>>;

/** Both choices for one client's workspaces: keeping one, or combining everything into it. */
export const duplicatesPreviewQuery = (keep: string, others: string[]) =>
  queryOptions({
    queryKey: ["admin", "workspaces", "duplicates", keep, ...others],
    queryFn: () => fetchDuplicatesPreview(keep, others),
  });

/** People an account could be merged into, by name or email. */
export const peopleSearchQuery = (q: string) =>
  queryOptions({
    queryKey: ["admin", "people", "search", q],
    queryFn: () => unwrap(api.admin.workspaces.users.search.$get({ query: { q } })),
  });

/** What merging one person's account into another would move. */
export const accountMergePreviewQuery = (userId: string, into: string) =>
  queryOptions({
    queryKey: ["admin", "people", userId, "merge-preview", into],
    queryFn: () =>
      unwrap(
        api.admin.workspaces.users[":userId"]["merge-preview"].$get({
          param: { userId },
          query: { into },
        }),
      ),
  });

/** After a change: this workspace's page, the Workspaces list and the home numbers are stale. */
export function useRefreshWorkspace(id: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces", id] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "yards"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "overview"] }),
      // People's pages list their workspaces by name and role.
      queryClient.invalidateQueries({ queryKey: ["admin", "people"] }),
    ]);
}
